<template>
  <div class="salary">
    <h2>Форма Заплата</h2>
    <!-- worker and month on the left, the buttons on the right -->
    <div class="salary-header">
      <div class="header-controlls">
        <!-- selectWorker() asks before leaving unsaved changes, so no v-model -->
        <SearchSelect
          :items="workers"
          :value="workerId"
          placeholder="Изберете служител"
          no-results-text="Няма намерени служители"
          @input="selectWorker"
        >
          <template #item="{ item }">
            <span class="saved-mark" :title="savedIds[item._id] ? 'Има запазена форма за месеца' : ''">
              {{ savedIds[item._id] ? "✓" : "" }}
            </span>
            {{ item.name }}
          </template>
        </SearchSelect>
        <datepicker
          :key="pickerKey"
          :language="bg"
          :minimum-view="'month'"
          :maximum-view="'year'"
          :format="'MM.yyyy'"
          :input-class="'salary-date'"
          :value="monthDate"
          @selected="selectMonth"
        ></datepicker>
      </div>
      <div class="btns" v-if="form">
        <button class="btn btn-new" @click="reloadCalculated()" title="Зарежда наново дните, ставките, обектите и остатъка отпуск">
          Презареди данните
        </button>
        <button class="btn btn-print" @click="print()">Печат</button>
      </div>
    </div>

    <p v-if="!workerId" class="salary-empty">Изберете служител и месец</p>

    <template v-if="form">
      <p class="salary-info">
        <span v-if="savedAt">Запазена на {{ formatDateTime(savedAt) }}.</span>
        <span v-else class="not-saved">Формата не е запазена - стойностите са изчислени от Форма 76, отпуските и ставките.</span>
        <span v-if="dirty" class="not-saved"> Има незапазени промени.</span>
      </p>
      <p v-if="!hasRates" class="salary-warning">
        Няма въведени ставки за {{ workerName }}. Ставките се въвеждат веднъж в
        <router-link to="/rates">Таблица за ставки</router-link>
        и се зареждат автоматично за всеки месец.
      </p>
      <p v-else-if="ratesChanged" class="salary-warning">
        Ставките в
        <router-link to="/rates">Таблица за ставки</router-link>
        са различни от тези в запазената форма. Натиснете „Презареди данните“, за да
        заредите новите.
      </p>

      <!-- the same block is printed -->
      <div class="salary-print" @input="dirty = true">
        <div class="print-title">
          <strong>{{ workerName }}</strong>
          <span>{{ monthName }} {{ form.year }} г.</span>
          <span>Работни дни: {{ legalWorkDays }}</span>
          <span>
            Остатък платен отпуск:
            {{ form.paidLeaveRemaining === null || form.paidLeaveRemaining === undefined ? "-" : form.paidLeaveRemaining }}
          </span>
        </div>

        <div class="salary-columns">
          <!-- НАЧИСЛЕНИЯ -->
          <table class="salary-table accruals">
            <thead>
              <tr>
                <th class="label">Начисления</th>
                <th class="num">Дни</th>
                <th class="num">Ставка</th>
                <th class="amount">Сума</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in accrualRows" :key="r.key" :class="{ 'print-hide': isEmptyExtra(r, form.accruals) }">
                <td class="label">
                  <div class="label-inner">
                    <span>{{ r.name }}</span>
                    <span
                      v-if="r.key === 'weekendBonus' && weekendBonusSuggested"
                      class="bonus-hint no-print"
                      title="Работил е поне 2 почивни дни (събота, неделя, национален празник) през месеца"
                    >✓ има право</span>
                    <input
                      v-if="r.comment"
                      class="comment"
                      type="text"
                      placeholder="Коментар"
                      v-model="form.accruals[r.comment]"
                    />
                    <span v-if="r.comment && form.accruals[r.comment]" class="print-value comment-value">
                      ({{ form.accruals[r.comment] }})
                    </span>
                  </div>
                </td>
                <td class="num">
                  <template v-if="r.day">
                    <input type="number" min="0" step="0.5" v-model.number="form.days[r.day]" />
                    <span class="print-value">{{ number(form.days[r.day]) }}</span>
                  </template>
                </td>
                <!-- the rates are edited only in Таблица за ставки -->
                <td class="rate" title="Ставките се променят в Таблица за ставки">
                  <template v-if="r.rate">{{ money(form.rates[r.rate]) }}</template>
                </td>
                <td class="amount">
                  <template v-if="r.manual">
                    <input type="number" min="0" step="0.01" v-model.number="form.accruals[r.key]" />
                    <span class="print-value">{{ money(form.accruals[r.key]) }}</span>
                  </template>
                  <template v-else>{{ money(accrual(r)) }}</template>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="label" colspan="3">Всичко начисления</td>
                <td class="amount">{{ money(totals.accruals) }}</td>
              </tr>
            </tfoot>
          </table>

          <!-- УДРЪЖКИ -->
          <table class="salary-table deductions">
            <thead>
              <tr>
                <th class="label">Удръжки</th>
                <th class="amount">Сума</th>
              </tr>
            </thead>
            <tbody>
              <tr class="group">
                <td class="label" colspan="2">Аванс</td>
              </tr>
              <tr v-for="r in deductionRows" :key="r.key" :class="{ 'print-hide': isEmptyExtra(r, form.deductions) }">
                <td class="label" :class="{ sub: r.group }">
                  <div class="label-inner">
                    <span>{{ r.name }}</span>
                    <input
                      v-if="r.comment"
                      class="comment"
                      type="text"
                      placeholder="Коментар"
                      v-model="form.deductions[r.comment]"
                    />
                    <span v-if="r.comment && form.deductions[r.comment]" class="print-value comment-value">
                      ({{ form.deductions[r.comment] }})
                    </span>
                  </div>
                </td>
                <td class="amount">
                  <input type="number" min="0" step="0.01" v-model.number="form.deductions[r.key]" />
                  <span class="print-value">{{ money(form.deductions[r.key]) }}</span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="label">Всичко удръжки</td>
                <td class="amount">{{ money(totals.deductions) }}</td>
              </tr>
            </tfoot>
          </table>

          <div class="salary-side">
            <!-- ОБЕКТИ -->
            <table class="salary-table objects">
              <thead>
                <tr>
                  <th class="label">Обекти</th>
                  <th class="amount">Дни</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(o, i) in form.objects" :key="`o-${i}`">
                  <td class="label">{{ o.name || "-" }}</td>
                  <td class="amount">{{ number(o.days) }}</td>
                </tr>
                <tr v-if="!form.objects.length">
                  <td class="label empty" colspan="2">Няма отчети за месеца</td>
                </tr>
              </tbody>
            </table>

            <!-- TOTALS -->
            <table class="salary-table totals">
              <tbody>
                <tr class="print-hide">
                  <td class="label">Всичко начисления</td>
                  <td class="amount">{{ money(totals.accruals) }}</td>
                </tr>
                <tr class="print-hide">
                  <td class="label">Всичко удръжки</td>
                  <td class="amount">{{ money(totals.deductions) }}</td>
                </tr>
                <tr>
                  <td class="label">Сума след удръжки</td>
                  <td class="amount">{{ money(totals.afterDeductions) }}</td>
                </tr>
                <tr>
                  <td class="label">Бонус</td>
                  <td class="amount">
                    <input type="number" min="0" step="0.01" v-model.number="form.bonus" />
                    <span class="print-value">{{ money(form.bonus) }}</span>
                  </td>
                </tr>
                <tr class="to-receive">
                  <td class="label">Остатък за получаване</td>
                  <td class="amount">{{ money(totals.toReceive) }}</td>
                </tr>
              </tbody>
            </table>

            <textarea class="salary-comment" placeholder="Бележка" v-model="form.comment"></textarea>
            <p v-if="form.comment" class="print-value print-comment">Бележка: {{ form.comment }}</p>
          </div>
        </div>
      </div>

      <!-- outside the printed block -->
      <div class="salary-footer">
        <button class="btn btn-save" @click="save()">Запази</button>
      </div>
    </template>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { bg } from "vuejs-datepicker/dist/locale";
import log from "./mixins/log.js";
import SearchSelect from "./shared/SearchSelect.vue";

const MONTHS = [
  "Януари", "Февруари", "Март", "Април", "Май", "Юни",
  "Юли", "Август", "Септември", "Октомври", "Ноември", "Декември",
];

const ACCRUAL_ROWS = [
  { key: "workday", name: "Делнични", day: "workday", rate: "workday" },
  { key: "weekend", name: "Събота / Неделя", day: "weekend", rate: "weekendHoliday" },
  { key: "holiday", name: "Национален празник", day: "holiday", rate: "weekendHoliday" },
  { key: "weekendBonus", name: "Бонус за работа в почивни дни", manual: true },
  { key: "paidLeave", name: "Платен отпуск", day: "paidLeave", rate: "paidLeave" },
  { key: "hospital", name: "Болничен (първите 2 дни)", day: "hospital", rate: "hospital" },
  { key: "tripNoOvernight", name: "Командировъчни без преспиване", day: "tripNoOvernight", rate: "tripNoOvernight" },
  { key: "tripOvernight", name: "Командировъчни с преспиване", day: "tripOvernight", rate: "tripOvernight" },
  { key: "other", name: "Друго", manual: true, comment: "otherComment" },
];

const DEDUCTION_ROWS = [
  // under the "Аванс" header
  { key: "voucher", name: "Ваучер", group: true },
  { key: "advance", name: "Аванс", group: true },
  { key: "bank", name: "По банка", group: true },
  { key: "credit", name: "Кредит", group: true },
  { key: "other1", name: "Друго", comment: "other1Comment" },
  { key: "other2", name: "Друго", comment: "other2Comment" },
  { key: "other3", name: "Друго", comment: "other3Comment" },
  { key: "other4", name: "Друго", comment: "other4Comment" },
];

function round(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default {
  mixins: [log],
  components: {
    Datepicker,
    SearchSelect,
  },
  data() {
    const now = new Date();
    return {
      bg: bg,
      workers: [],
      savedIds: {},
      workerId: this.$route.query.worker || "",
      monthDate: this.parseMonth(this.$route.query.month) || new Date(now.getFullYear(), now.getMonth(), 1),
      form: null,
      calculated: null,
      savedAt: null,
      legalWorkDays: 0,
      weekendBonusSuggested: false,
      hasRates: true,
      dirty: false,
      // re-created to put the picker back when the month change is cancelled
      pickerKey: 0,
      accrualRows: ACCRUAL_ROWS,
      deductionRows: DEDUCTION_ROWS,
    };
  },
  computed: {
    month() {
      const d = new Date(this.monthDate);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    },
    monthName() {
      return MONTHS[new Date(this.monthDate).getMonth()];
    },
    workerName() {
      const w = this.workers.find((w) => w._id === this.workerId);
      return w ? w.name : "";
    },
    // the same as calculateTotals() in server/helpers/salary.js
    // a saved form keeps its rates; true when Таблица за ставки has others now
    ratesChanged() {
      if (!this.form || !this.calculated || !this.savedAt) return false;
      return Object.keys(this.calculated.rates).some(
        (k) => round(this.calculated.rates[k]) !== round(this.form.rates[k])
      );
    },
    totals() {
      if (!this.form) return {};
      const accruals = round(this.accrualRows.reduce((s, r) => s + this.accrual(r), 0));
      const deductions = round(this.deductionRows.reduce((s, r) => s + (Number(this.form.deductions[r.key]) || 0), 0));
      const afterDeductions = round(accruals - deductions);
      return {
        accruals,
        deductions,
        afterDeductions,
        toReceive: round(afterDeductions + (Number(this.form.bonus) || 0)),
      };
    },
  },
  methods: {
    parseMonth(m) {
      const match = /^(\d{4})-(\d{2})$/.exec(m || "");
      return match ? new Date(Number(match[1]), Number(match[2]) - 1, 1) : null;
    },
    money(n) {
      return round(n).toFixed(2);
    },
    // days: up to 2 decimals, no trailing zeros
    number(n) {
      return String(round(n));
    },
    // a "Друго" row with no amount and no comment - not printed
    isEmptyExtra(r, values) {
      return !!r.comment && !Number(values[r.key]) && !values[r.comment];
    },
    formatDateTime(d) {
      d = new Date(d);
      const p = (n) => String(n).padStart(2, "0");
      return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },
    errorMessage(err) {
      return (err.response && err.response.data && err.response.data.failed) || "Нещо се обърка";
    },
    // days x rate, or the amount typed by hand
    accrual(r) {
      if (r.manual) return Number(this.form.accruals[r.key]) || 0;
      return round((Number(this.form.days[r.day]) || 0) * (Number(this.form.rates[r.rate]) || 0));
    },
    confirmLeave() {
      return !this.dirty || window.confirm("Има незапазени промени. Да продължа ли без да ги запазя?");
    },
    selectWorker(id) {
      if (!this.confirmLeave()) return;
      this.workerId = id;
      this.updateQuery();
      this.getForm();
    },
    selectMonth(d) {
      if (!this.confirmLeave()) {
        this.pickerKey++;
        return;
      }
      this.monthDate = new Date(d.getFullYear(), d.getMonth(), 1);
      this.updateQuery();
      this.getSaved();
      this.getForm();
    },
    updateQuery() {
      const query = { month: this.month };
      if (this.workerId) query.worker = this.workerId;
      if (query.month !== this.$route.query.month || query.worker !== this.$route.query.worker) {
        this.$router.replace({ query });
      }
    },
    getWorkers() {
      return this.$http
        .get(`${process.env.VUE_APP_BASE_URI}/salary/workers`)
        .then((response) => {
          this.workers = response.data;
        })
        .catch((err) => {
          console.log(err);
          this.$snotify.error(this.errorMessage(err));
        });
    },
    getSaved() {
      return this.$http
        .get(`${process.env.VUE_APP_BASE_URI}/salary/list/${this.month}`)
        .then((response) => {
          const ids = {};
          response.data.forEach((s) => (ids[s.workerId] = true));
          this.savedIds = ids;
        })
        .catch((err) => console.log(err));
    },
    getForm() {
      this.form = null;
      this.dirty = false;
      if (!this.workerId) return;

      this.$store.dispatch("startLoading");
      this.$http
        .get(`${process.env.VUE_APP_BASE_URI}/salary/form/${this.workerId}/${this.month}`)
        .then((response) => {
          const data = response.data;
          this.calculated = data.calculated;
          this.legalWorkDays = data.legalWorkDays;
          this.weekendBonusSuggested = data.weekendBonusSuggested;
          this.hasRates = data.hasRates;
          this.savedAt = data.saved ? data.saved.updatedAt : null;
          this.form = this.$cloneDeep(data.saved || data.calculated);
          if (!this.form.objects) this.form.objects = [];
        })
        .catch((err) => {
          console.log(err);
          this.$snotify.error(this.errorMessage(err));
        })
        .then(() => this.$store.dispatch("stopLoading"));
    },
    // the days, rates, objects and leave balance from the current data;
    // the amounts typed by hand (bonus, друго, удръжки) stay
    reloadCalculated() {
      if (!this.calculated) return;
      const c = this.$cloneDeep(this.calculated);
      this.form.days = c.days;
      this.form.rates = c.rates;
      this.form.objects = c.objects;
      this.form.paidLeaveRemaining = c.paidLeaveRemaining;
      this.dirty = true;
      this.$snotify.info("Данните са презаредени - запазете формата");
    },
    save() {
      const payload = this.$cloneDeep(this.form);
      this.accrualRows.forEach((r) => (payload.accruals[r.key] = this.accrual(r)));

      const invalid = []
        .concat(Object.values(payload.days), Object.values(payload.rates), this.accrualRows.map((r) => payload.accruals[r.key]))
        .concat(this.deductionRows.map((r) => payload.deductions[r.key]), [payload.bonus])
        .some((v) => v !== "" && v !== null && v !== undefined && (!isFinite(Number(v)) || Number(v) < 0));
      if (invalid) {
        return this.$snotify.error("Невалидни стойности - въведете числа, по-големи или равни на 0");
      }

      this.$store.dispatch("startLoading");
      this.$http
        .put(`${process.env.VUE_APP_BASE_URI}/salary/form/${this.workerId}/${this.month}`, payload)
        .then((response) => {
          this.form = this.$cloneDeep(response.data);
          if (!this.form.objects) this.form.objects = [];
          this.savedAt = response.data.updatedAt;
          this.dirty = false;
          this.$set(this.savedIds, this.workerId, true);
          this.$snotify.success("Формата е запазена");
          this.newLog("editSalary", `${this.workerName} - ${this.monthName} ${this.form.year}`);
        })
        .catch((err) => {
          console.log(err);
          this.$snotify.error(this.errorMessage(err));
        })
        .then(() => this.$store.dispatch("stopLoading"));
    },
    // the browser names the PDF after the page title: "Заплата - <име> - <месец> <година>"
    print() {
      const title = document.title;
      const restore = () => {
        document.title = title;
        window.removeEventListener("afterprint", restore);
      };
      window.addEventListener("afterprint", restore);
      document.title = `Заплата - ${this.workerName} - ${this.monthName} ${this.form.year}`;
      window.print();
    },
    beforeUnload(e) {
      if (!this.dirty) return;
      e.preventDefault();
      e.returnValue = "";
    },
  },
  beforeRouteLeave(to, from, next) {
    next(this.confirmLeave());
  },
  mounted() {
    window.addEventListener("beforeunload", this.beforeUnload);
    this.getWorkers();
    this.getSaved();
    this.getForm();
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.beforeUnload);
  },
};
</script>

<style scoped>
.salary {
  background-color: #f0f7f7;
  min-height: calc(100vh - 100px);
  padding: 10px 40px;
}
.salary-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.header-controlls {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.salary-header .btns {
  display: flex;
  flex-wrap: wrap;
}
.saved-mark {
  color: #2e7d32;
  flex-shrink: 0;
  width: 16px;
}
.salary >>> .salary-date {
  background-color: #c9d9d9;
  border: 1px solid #c9d9d9;
  border-radius: 4px;
  cursor: pointer;
  height: 36px;
  padding: 0 10px;
  width: 100px;
}
.print-value {
  display: none;
}
.salary-footer {
  display: flex;
  margin-top: 10px;
}
.salary-footer .btn {
  margin-left: 0;
}
.btn-save {
  background-color: #083f75;
}
.btn-save:hover {
  background-color: #0d5194;
}
.salary-info {
  font-size: 12px;
  margin: 0 0 10px;
}
.salary-warning {
  background-color: #fff4e5;
  border: 1px solid #f3c58a;
  border-radius: 4px;
  font-size: 12px;
  margin: 0 0 10px;
  padding: 8px 10px;
}
.salary-warning a {
  color: #083f75;
  font-weight: 700;
}
.not-saved {
  color: #9c5700;
}
.salary-empty {
  padding: 40px 0;
  text-align: center;
}
.salary-print {
  background-color: #fff;
  padding: 10px;
}
.print-title {
  align-items: baseline;
  border-bottom: 1px solid #c9d9d9;
  display: flex;
  flex-wrap: wrap;
  font-size: 13px;
  gap: 20px;
  margin-bottom: 8px;
  padding-bottom: 6px;
}
.print-title strong {
  font-size: 15px;
}
/* 3 even columns; the accruals and deductions tables have the same rows */
.salary-columns {
  align-items: start;
  display: grid;
  gap: 15px;
  grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr);
}
.salary-side {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.salary-table {
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
  width: 100%;
}
.salary-table th,
.salary-table td {
  border: 1px solid #c9d9d9;
  height: 32px;
  padding: 0 6px;
  text-align: center;
}
.salary-table th {
  background-color: #e8eeee;
  height: 28px;
}
.salary-table .label {
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.salary-table .label-inner {
  align-items: center;
  display: flex;
  gap: 6px;
}
.salary-table .label-inner > span {
  flex-shrink: 0;
}
.salary-table .num {
  width: 70px;
}
.salary-table .rate {
  color: #444;
  text-align: right;
}
.salary-table .amount {
  text-align: right;
  width: 90px;
}
.salary-table input {
  border: 1px solid #c9d9d9;
  border-radius: 3px;
  box-sizing: border-box;
  height: 24px;
  padding: 0 4px;
  text-align: right;
  width: 100%;
}
.salary-table input.comment {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.salary-table .group td {
  background-color: #f4f8f8;
  font-weight: 700;
}
.salary-table .sub {
  padding-left: 18px;
}
.salary-table tfoot td,
.salary-table .to-receive td {
  background-color: #deebf7;
  font-weight: 700;
}
.salary-table .empty {
  color: #8a8a8a;
}
.bonus-hint {
  color: #2e7d32;
  font-size: 11px;
}
.salary-comment {
  border: 1px solid #c9d9d9;
  border-radius: 3px;
  font-size: 12px;
  min-height: 40px;
  padding: 4px;
}

@media screen and (max-width: 1000px) {
  .salary {
    padding: 10px 15px;
  }
  .salary-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
  .salary-columns {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>

<style>
/* A4 portrait, the tables one under the other; values instead of the inputs */
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }
  body * {
    visibility: hidden;
  }
  .salary-print,
  .salary-print * {
    visibility: visible;
  }
  .salary-print {
    box-sizing: border-box;
    left: 0;
    padding: 14mm 18mm !important;
    position: absolute;
    top: 0;
    width: 210mm;
  }
  .salary-print input,
  .salary-print textarea,
  .salary-print .no-print,
  .salary-print .print-hide {
    display: none !important;
  }
  .salary-print .print-value {
    display: inline;
  }
  .salary-print .print-title {
    border-bottom: 0.4mm solid #083f75 !important;
    font-size: 10pt !important;
    gap: 8mm !important;
    margin-bottom: 5mm !important;
    padding-bottom: 2mm !important;
  }
  .salary-print .print-title strong {
    font-size: 13pt !important;
  }
  .salary-print .salary-columns,
  .salary-print .salary-side {
    display: flex !important;
    flex-direction: column;
    gap: 5mm !important;
  }
  .salary-print .salary-table {
    font-size: 9pt !important;
    table-layout: fixed;
    width: 100%;
  }
  .salary-print .salary-table th,
  .salary-print .salary-table td {
    height: 6mm !important;
    padding: 0 2mm !important;
  }
  .salary-print .salary-table th {
    background-color: #e8eeee !important;
    text-align: left;
  }
  .salary-print .salary-table .num {
    text-align: right;
    width: 22mm !important;
  }
  .salary-print .salary-table .rate,
  .salary-print .salary-table .amount {
    text-align: right;
    width: 28mm !important;
  }
  .salary-print .salary-table .sub {
    padding-left: 6mm !important;
  }
  .salary-print .comment-value {
    color: #555;
  }
  .salary-print .print-comment {
    font-size: 9pt;
    margin: 0;
  }
  .salary-print .salary-table tfoot td,
  .salary-print .salary-table .to-receive td,
  .salary-print .salary-table .group td {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .salary-print .salary-table .to-receive td {
    font-size: 10pt;
  }
}
</style>
